import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (_e) { toast.error("Could not send"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-accent)]">Get in touch</h1>
      <p className="text-slate-500 mt-2">We'd love to hear from you. Drop us a line and our team will reach out.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <Card className="p-6 lg:col-span-1 space-y-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" />
            <div><div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Email</div><div className="text-[var(--brand-accent)]">{settings.contact_email}</div></div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" />
            <div><div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Phone</div><div className="text-[var(--brand-accent)]">{settings.contact_phone}</div></div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" />
            <div><div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Address</div><div className="text-[var(--brand-accent)]">{settings.contact_address}</div></div>
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Subject</Label><Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Message</Label><Textarea data-testid="contact-message" rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
            <div className="md:col-span-2"><Button data-testid="contact-submit" disabled={busy} type="submit" className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">{busy ? "Sending..." : "Send message"}</Button></div>
          </form>
        </Card>
      </div>
    </div>
  );
}
